import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Home, Zap, Thermometer, ChefHat, Users } from "lucide-react";

interface HouseholdSurveyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

export const HouseholdSurveyDialog = ({ open, onOpenChange, userId }: HouseholdSurveyDialogProps) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    household_size: 2,
    has_electric_heating: false,
    has_air_conditioning: false,
    has_electric_cooking: false,
    refrigerator_count: 1,
    washing_machine_type: 'electric',
    building_type: 'apartment',
    square_footage: 1000,
    occupancy_pattern: {
      work_from_home: false,
      typical_home_hours: 'evenings'
    }
  });
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!userId) {
      console.error('No user ID provided');
      toast({
        title: "Error",
        description: "User ID is missing. Please log in and try again.",
        variant: "destructive"
      });
      return;
    }

    // Skip saving for demo user (not authenticated)
    if (userId === '11111111-2222-3333-4444-555555555555') {
      toast({
        title: "Demo Mode",
        description: "Profile updates are not saved for demo users. Please create an account to save your preferences.",
      });
      onOpenChange(false);
      return;
    }

    try {
      console.log('Submitting household profile for user:', userId);
      console.log('Form data:', formData);
      
      const { error } = await supabase
        .from('user_household_profiles')
        .upsert({
          user_id: userId,
          household_size: formData.household_size,
          has_electric_heating: formData.has_electric_heating,
          has_air_conditioning: formData.has_air_conditioning,
          has_electric_cooking: formData.has_electric_cooking,
          refrigerator_count: formData.refrigerator_count,
          washing_machine_type: formData.washing_machine_type,
          building_type: formData.building_type,
          square_footage: formData.square_footage,
          occupancy_pattern: formData.occupancy_pattern,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving household profile:', error);
        toast({
          title: "Error",
          description: "Failed to save household profile. Please ensure you're logged in with a valid account.",
          variant: "destructive"
        });
        return;
      }
      
      console.log('Household profile saved successfully');

      // Trigger baseline calculation
      const { error: baselineError } = await supabase.functions.invoke('calculate-baseline-load', {
        body: { userId }
      });

      if (baselineError) {
        console.warn('Error calculating baseline:', baselineError);
      }

      toast({
        title: "Profile Saved",
        description: "Your household profile has been saved. We'll use this to provide better energy insights.",
      });

      onOpenChange(false);
    } catch (error: unknown) {
      console.error('Unexpected error in handleSubmit:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    }
  };

  const nextStep = () => setStep(s => Math.min(s + 1, 4));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Household Profile Setup ({step}/4)
          </DialogTitle>
          <DialogDescription>
            Help us understand your home to provide better energy insights
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Household Information</h3>
              </div>
              
              <div className="space-y-2">
                <Label>How many people live in your home?</Label>
                <RadioGroup 
                  value={formData.household_size.toString()} 
                  onValueChange={(v) => setFormData({...formData, household_size: parseInt(v)})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1" id="size1" />
                    <Label htmlFor="size1">1-2 people</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="3" id="size3" />
                    <Label htmlFor="size3">3-4 people</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="5" id="size5" />
                    <Label htmlFor="size5">5+ people</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>Building Type</Label>
                <RadioGroup 
                  value={formData.building_type} 
                  onValueChange={(v) => setFormData({...formData, building_type: v})}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="apartment" id="apartment" />
                    <Label htmlFor="apartment">Apartment</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="house" id="house" />
                    <Label htmlFor="house">House</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sqft">Approximate Square Footage</Label>
                <Input 
                  id="sqft"
                  type="number" 
                  value={formData.square_footage}
                  onChange={(e) => setFormData({...formData, square_footage: parseInt(e.target.value)})}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Thermometer className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Heating & Cooling</h3>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="electric-heating"
                  checked={formData.has_electric_heating}
                  onCheckedChange={(checked) => setFormData({...formData, has_electric_heating: checked as boolean})}
                />
                <Label htmlFor="electric-heating">I have electric heating</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="ac"
                  checked={formData.has_air_conditioning}
                  onCheckedChange={(checked) => setFormData({...formData, has_air_conditioning: checked as boolean})}
                />
                <Label htmlFor="ac">I have air conditioning</Label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <ChefHat className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Kitchen & Laundry</h3>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="electric-cooking"
                  checked={formData.has_electric_cooking}
                  onCheckedChange={(checked) => setFormData({...formData, has_electric_cooking: checked as boolean})}
                />
                <Label htmlFor="electric-cooking">I use electric stove/oven for cooking</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fridges">Number of Refrigerators/Freezers</Label>
                <Input 
                  id="fridges"
                  type="number" 
                  min="0"
                  max="5"
                  value={formData.refrigerator_count}
                  onChange={(e) => setFormData({...formData, refrigerator_count: parseInt(e.target.value)})}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="washing"
                  checked={formData.washing_machine_type === 'electric'}
                  onCheckedChange={(checked) => setFormData({...formData, washing_machine_type: checked ? 'electric' : 'none'})}
                />
                <Label htmlFor="washing">I have a washing machine</Label>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Occupancy Pattern</h3>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="wfh"
                  checked={formData.occupancy_pattern.work_from_home}
                  onCheckedChange={(checked) => setFormData({
                    ...formData, 
                    occupancy_pattern: {...formData.occupancy_pattern, work_from_home: checked as boolean}
                  })}
                />
                <Label htmlFor="wfh">Someone works from home regularly</Label>
              </div>

              <div className="space-y-2">
                <Label>When are people typically home?</Label>
                <RadioGroup 
                  value={formData.occupancy_pattern.typical_home_hours}
                  onValueChange={(v) => setFormData({
                    ...formData, 
                    occupancy_pattern: {...formData.occupancy_pattern, typical_home_hours: v}
                  })}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all_day" id="all_day" />
                    <Label htmlFor="all_day">All day</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="evenings" id="evenings" />
                    <Label htmlFor="evenings">Evenings and weekends</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="minimal" id="minimal" />
                    <Label htmlFor="minimal">Minimal (mostly away)</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={prevStep} disabled={step === 1}>
            Previous
          </Button>
          {step < 4 ? (
            <Button onClick={nextStep}>Next</Button>
          ) : (
            <Button onClick={handleSubmit}>Complete Setup</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
